import { useEffect, useRef, useState, type ReactElement } from "react";
import { MEMBER_CHANGE_TOUCH_ID_ON_MAC } from "@cleo/shared/memberChangeCard";

import {
  decideCloudGate,
  worldWords,
  type CloudGateWait,
  type WaitingApprovals,
} from "~/neucleos/cloudConversations";
import {
  createGateDecider,
  gateCardModel,
  type GateCardModel,
  type GateLine,
} from "~/neucleos/cloudConversationGate";
import { useWorldConvexClient } from "~/neucleos/worldConvexClient";
import { cn } from "~/lib/utils";
import { isWebHost } from "~/webHost";
import { OpenMembersCardButton } from "./chat/ComposerPendingApprovalActions";

// The approval card of a conversation kept in the World, in the browser's conversation pane (Can 47's approval clause
// on the web): Pocket's card (apps/mobile PendingGateCard.tsx, cloudConversationGateCard.tsx) in the Desk's own
// clothes. It shows what Cleo will do and decides exactly that gate instance, once, through the World's own door.

const STATUS_LABEL: Record<GateCardModel["status"], string> = {
  loading: "Checking this approval…",
  awaiting: "Approval needed",
  deciding: "Sending your answer…",
  approved: "Approved",
  declined: "Declined",
  "failed-load": "Could not check",
  stale: "No longer waiting",
  cancelled: "Cancelled",
};

const PREVIEW_LINES = 5;

/** How long after Approve opened the rest a further Approve is ignored, so a double press cannot decide unread. */
const APPROVE_AGAIN_MS = 400;

/** How long a card waits for the World's snapshot before it says it could not check, and offers to try again. */
export const GATE_LOAD_MS = 10_000;

/**
 * One gate-wait of the conversation. It acts only when it IS the World's current wait (`waiting.current`, from
 * waitingApprovals): then it shows the exact call the World verified and decides that approval, naming that
 * occurrence. Any other card is stale and offers no answer. A decision names the conversation, the approval's key and
 * digest and the occurrence, and is made once.
 */
export function CloudGateCard({
  conversationId,
  worldKey = null,
  wait,
  waiting,
  onRetry,
}: {
  readonly conversationId: string;
  readonly worldKey?: string | null;
  readonly wait: CloudGateWait;
  /** The World's snapshot of what the conversation waits on; undefined until it has answered. */
  readonly waiting: WaitingApprovals | undefined;
  /** Asks the World for the snapshot again. */
  readonly onRetry?: () => void;
}): ReactElement {
  const client = useWorldConvexClient();
  const [loadFailed, setLoadFailed] = useState(false);
  // Each attempt gets its own ten seconds: Try again starts the wait again.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (waiting !== undefined) {
      setLoadFailed(false);
      return;
    }
    const timer = setTimeout(() => setLoadFailed(true), GATE_LOAD_MS);
    return () => clearTimeout(timer);
  }, [waiting, attempt]);
  const base = gateCardModel({ wait, waiting, loadFailed, deciding: false, error: null });
  // What the person has done on THIS approval; another approval (a renewal) starts clean.
  const approvalKey = base.gateInstanceKey ? `${base.gateInstanceKey}:${base.targetDigest}` : null;
  const [local, setLocal] = useState<{
    key: string | null;
    deciding: boolean;
    error: string | null;
    stale?: boolean;
  }>({ key: null, deciding: false, error: null });
  const mine =
    local.key === approvalKey ? local : { key: approvalKey, deciding: false, error: null };
  const decider = useRef<{ key: string; value: ReturnType<typeof createGateDecider> } | null>(null);
  const model = gateCardModel({
    wait,
    waiting,
    loadFailed,
    refusedStale: mine.stale === true,
    deciding: mine.deciding,
    error: mine.error,
  });
  return (
    <GateCardView
      model={model}
      worldKey={worldKey}
      onRetry={() => {
        setLoadFailed(false);
        setAttempt((n) => n + 1);
        onRetry?.();
      }}
      onDecide={(outcome) => {
        if (model.status !== "awaiting" || client === null) return;
        if (!model.gateInstanceKey || !model.occurrence || approvalKey === null) return;
        // Approve needs the World's exact call on screen; Decline never does.
        if (outcome === "approved" && !model.detailsReady) return;
        const occurrence = model.occurrence;
        if (decider.current?.key !== approvalKey)
          decider.current = {
            key: approvalKey,
            value: createGateDecider({
              gateInstanceKey: model.gateInstanceKey,
              // The digest the approval itself holds: the decision binds to that exact call.
              targetDigest: model.targetDigest,
              occurrence: {
                turnKey: occurrence.turnKey,
                step: occurrence.step,
                toolRunId: occurrence.toolRunId,
              },
              ...(model.shownCard ? { shownCard: model.shownCard } : {}),
              send: (input) => decideCloudGate(client, { conversationId, ...input }),
              onError: (cause) =>
                setLocal({
                  key: approvalKey,
                  deciding: false,
                  error: worldWords(cause),
                  // The World says this is not the wait it holds: the card is stale now, before the snapshot says so.
                  stale: /not waiting on that approval/u.test(
                    String((cause as { data?: unknown })?.data ?? ""),
                  ),
                }),
            }),
          };
        setLocal({ key: approvalKey, deciding: true, error: null });
        void decider.current.value.decide(outcome);
      }}
    />
  );
}

/** The card itself: what it shows and the two answers. Pure of the World; its state is what the person has opened. */
export function GateCardView({
  model,
  worldKey = null,
  onDecide,
  onRetry,
}: {
  readonly model: GateCardModel;
  readonly worldKey?: string | null;
  readonly onDecide: (outcome: "approved" | "declined") => void;
  /** Offered only when the World's answer could not be had. */
  readonly onRetry?: (() => void) | undefined;
}): ReactElement {
  const open = model.status === "awaiting";
  const macHost = !isWebHost();
  // Everything Cleo will do is readable. A value is never clipped; only a long list of fields starts folded to its
  // first few, with a button that shows the rest. What the person opened is bound to exactly what is shown: the
  // approval and every displayed value. Any change of either (a history gap filling in, an approval renewed) starts
  // again folded and unacknowledged.
  const contentKey = JSON.stringify([
    model.gateInstanceKey,
    model.targetDigest,
    model.lines.map((line) => [line.label, line.value]),
    model.exact.map((line) => [line.label, line.value]),
  ]);
  const [exactOpen, setExactOpen] = useState<string | null>(null);
  const [seen, setSeen] = useState<{
    key: string;
    all: boolean;
    fromApprove?: boolean;
    at?: number;
  }>({ key: "", all: false });
  const opened = seen.key === contentKey;
  const all = opened && seen.all;
  const hidden = model.lines.length > PREVIEW_LINES;
  const shown = all ? model.lines : model.lines.slice(0, PREVIEW_LINES);
  // Approve needs the exact call's details; Decline does not. While part of the call is still folded, Approve opens
  // the rest instead of approving, and the next Approve decides.
  const canApprove = open && model.detailsReady && (!hidden || opened);
  const approveOpensRest = open && model.detailsReady && hidden && !opened;
  const askedAgain = opened && seen.fromApprove === true;
  const more = model.lines.length - PREVIEW_LINES;
  return (
    <div
      className="space-y-2 rounded-xl border border-border bg-muted/40 p-3"
      data-cloud-gate-card=""
      data-cloud-gate-status={model.status}
    >
      <p
        className="text-[10px] font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300"
        data-cloud-gate-status-words=""
      >
        {STATUS_LABEL[model.status]}
      </p>
      <p className="text-sm font-semibold" data-cloud-gate-title="">
        {model.title}
      </p>
      <p className="text-xs text-muted-foreground" data-cloud-gate-action="">
        Cleo wants to: {model.action}
      </p>
      {shown.map((line) => (
        <GateLineView key={`${line.label}:${line.value}`} line={line} exact={false} />
      ))}
      {hidden ? (
        <button
          className="text-xs text-sky-700 hover:underline dark:text-sky-300"
          data-cloud-gate-expand=""
          onClick={() => setSeen({ key: contentKey, all: !all })}
          type="button"
        >
          {all
            ? "Show fewer details"
            : `Show everything Cleo will do (${more} more detail${more === 1 ? "" : "s"})`}
        </button>
      ) : null}
      {model.exact.length > 0 ? (
        <button
          className="text-xs text-sky-700 hover:underline dark:text-sky-300"
          data-cloud-gate-exact=""
          onClick={() => setExactOpen(exactOpen === contentKey ? null : contentKey)}
          type="button"
        >
          {exactOpen === contentKey ? "Hide the exact request" : "Show the exact request"}
        </button>
      ) : null}
      {exactOpen === contentKey
        ? model.exact.map((line) => (
            <GateLineView key={`exact:${line.label}:${line.value}`} line={line} exact />
          ))
        : null}
      {askedAgain && canApprove ? (
        <p className="text-xs" data-cloud-gate-approve-again="">
          Here is everything Cleo will do. Press Approve again to let her.
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground" data-cloud-gate-effect="">
        {model.approveOnCard && !macHost ? MEMBER_CHANGE_TOUCH_ID_ON_MAC : model.effect}
      </p>
      {model.error ? (
        <p className="text-xs text-destructive" data-cloud-gate-error="" role="alert">
          {model.error}
        </p>
      ) : null}
      {model.status === "failed-load" && onRetry ? (
        <button
          className="rounded-md border border-border px-3 py-1 text-xs hover:bg-accent"
          data-cloud-gate-retry=""
          onClick={onRetry}
          type="button"
        >
          Try again
        </button>
      ) : null}
      {open || model.status === "deciding" ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {model.approveOnCard && macHost ? (
            <OpenMembersCardButton
              worldKey={worldKey}
              gateInstanceKey={model.gateInstanceKey}
              disabled={!open}
            />
          ) : (
            <button
              aria-label="Approve"
              className={cn(
                "rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground hover:opacity-90",
                !canApprove && !approveOpensRest ? "opacity-50" : "",
              )}
              data-cloud-gate-approve=""
              disabled={!canApprove && !approveOpensRest}
              onClick={() => {
                if (approveOpensRest) {
                  setSeen({ key: contentKey, all: true, fromApprove: true, at: Date.now() });
                  return;
                }
                // The press that opened the rest is not an answer: a second press within a moment is ignored.
                if (askedAgain && seen.at !== undefined && Date.now() - seen.at < APPROVE_AGAIN_MS)
                  return;
                onDecide("approved");
              }}
              title={approveOpensRest ? "Opens the rest of what Cleo will do first" : undefined}
              type="button"
            >
              Approve
            </button>
          )}
          <button
            aria-label="Decline"
            className="rounded-md border border-border px-3 py-1 text-xs text-destructive hover:bg-accent disabled:opacity-60"
            data-cloud-gate-decline=""
            disabled={!open}
            onClick={() => onDecide("declined")}
            type="button"
          >
            Decline
          </button>
        </div>
      ) : null}
    </div>
  );
}

function GateLineView({
  line,
  exact,
}: {
  readonly line: GateLine;
  readonly exact: boolean;
}): ReactElement {
  return (
    <div data-cloud-gate-line={exact ? "exact" : ""}>
      <p className="text-[10px] font-semibold uppercase text-muted-foreground">{line.label}</p>
      <p className="whitespace-pre-wrap break-words text-xs">{line.value}</p>
    </div>
  );
}
